import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Container, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../app/store';
import Header from '../components/Header';
import Loading from '../components/Loading';
import { addGuest, Guest, removeGuest, reset, update, updateGuest } from '../features/inputSlice';
import { getGuests, getSelectBoxes } from '../services/ApiService';
import { Guest as DtoGuest, RsvAccountGetData } from '../services/dto/rsvAccountDto';
import { SelectBox } from '../services/dto/selectboxDto';

const SCREEN = 'input';

const Input = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const errorRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const input = useSelector((state: RootState) => state.input);

  const [countries, setCountries] = useState<SelectBox[]>([]);
  const [prefectures, setPrefectures] = useState<SelectBox[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // selectboxデータ取得
        const {countries, provices} = await getSelectBoxes();
        if (countries !== undefined) {
          setCountries(countries);
        } else {
          console.log("Failed to fetch countries");
        }
        if (provices !== undefined) {
          setPrefectures(provices);
        } else {
          console.log("Failed to fetch countries");
        }

        if (input.guests.length === 0) {
          // ゲストデータ取得
          await getGuestData();
        }
      } catch (error) {
        console.error("Error fetching Redis data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch(update({ key: name, value: value }));
  };

  const handleChangeGuest = async (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const { name, value } = e.target;
    dispatch(updateGuest({ id, changes: { [name]: value } }));

    if (name === 'postNo' && value.length === 7) {
      // 住所取得
      try {
        const response = await axios.get(`https://zip-cloud.appspot.com/api/search?zipcode=${value}`);

        if (response.data.results && response.data.results.length > 0) {
          console.log(response.data.results);
          const { address1, address2, address3 } = response.data.results[0];
          const prefecture = prefectures.find(prefecture => prefecture.name === address1)
          dispatch(updateGuest({ id, changes: { provinceId: prefecture ? prefecture.id : null } }));
          dispatch(updateGuest({ id, changes: { address1: address2 + address3 } }));
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleChangeSelect = async (e: SelectChangeEvent<number | null>, id: number) => {
    const { name, value } = e.target;
    dispatch(updateGuest({ id, changes: { [name]: value } }));
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    const isValid = input.guests.every((guest) =>
      guest.altNm !== null && guest.altNm.trim() !== '' &&
      guest.firstNm !== null && guest.firstNm.trim() !== '' &&
      guest.contact !== null && guest.contact.trim() !== '' &&
      guest.rcountryId !== null &&
      guest.countryId !== null &&
      guest.postNo !== null && guest.postNo.trim() !== '' &&
      (guest.provinceId !== null || (guest.countryId !== 1604 && guest.countryId !== 1711)) &&
      guest.address1 !== null && guest.address1.trim() !== '' &&
      guest.email !== null && guest.email.trim() !== ''
    );

    if (!isValid) {
      setError('全ての必須項目を入力してください。');
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    // redisに都道府県データが存在しない国を選択 -> 都道府県を空にする
    if (input.guests.length === 0) {
      return;
    }

    input.guests.forEach((guest: Guest, index: number) => {
      const exists = prefectures.some(pref => pref.grpId === guest.countryId)
      if (!exists) {
        dispatch(updateGuest({ id: index, changes: { provinceId: null } }));
      }
    });

    // 1人でも国籍と居住国が日本以外のゲストがいるかで遷移先変更
    const hasNonJpnGuest = input.guests.some((guest: Guest) =>
      guest.rcountryId !== 1604 && guest.countryId !== 1604
    );
    const nextTo = hasNonJpnGuest ? "/certificate" : "/signature";
    navigate(nextTo);
  };

  const handleAddGuest = () => {
    const newGuest: Guest = {
      id: input.guests.length,
      guestId: null,
      altNm: '',
      firstNm: '',
      contact: '',
      rcountryId: 1604,
      countryId: 1604,
      postNo: '',
      provinceId: null,
      address1: '',
      email: '',
      certificate: '',
      signature: '',
      version: 0,
    };
    dispatch(addGuest(newGuest));
  }

  const deleteAccordion = (id: number) => {
    dispatch(removeGuest(id));
  };

  const createGuest = (dto: DtoGuest, index: number): Guest => {
    return {
      id: index,
      guestId: dto.guestId,
      altNm: dto.altNm,
      firstNm: dto.firstNm,
      contact: dto.contact,
      rcountryId: dto.rcountryId ?? 1604,
      countryId: dto.countryId ?? 1604,
      postNo: dto.postNo,
      provinceId: dto.provinceId,
      address1: dto.address1,
      email: dto.email,
      certificate: dto.certificate,
      signature: '',
      version: dto.version,
    }
  }

  /**
   * ゲストデータ取得
   */
  const getGuestData = async () => {
    try {
      dispatch(reset());
      const res = await getGuests();
      const data: RsvAccountGetData = res.data;

      // 取得データをsliceに保存
      Object.keys(data).forEach(key => {
        if (key !== 'guests') {
          dispatch(update({ key: key, value: data[key as keyof RsvAccountGetData] }));
        }
      });
      data.guests.forEach((dto: DtoGuest, index: number) => {
        const guest: Guest = createGuest(dto, index);
        dispatch(addGuest(guest));
      });
    } catch (error) {
      console.error('Error fetching guest data:', error);
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} style={{ padding: '2rem', marginTop: '2rem' }}>
        <Header title={t(`${SCREEN}.title`)} />
        <Loading open={loading} />
        <div ref={errorRef}>
          <FormHelperText
            error
            style={{ color: 'red', marginTop: '16px' }}
          >
            {error}
          </FormHelperText>
        </div>
        <Box mt={2}>
          {input.guests.map((guest, index) =>
            <div key={guest.id}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {t(`${SCREEN}.guest`)}{index + 1}
                  {guest.id !== 0 && !guest.guestId &&
                    <IconButton
                      aria-label="close"
                      size="small"
                      onClick={() => deleteAccordion(guest.id)}
                      style={{ marginLeft: 'auto' }}
                    >
                      <CloseIcon />
                    </IconButton>
                  }
                </AccordionSummary>
                <AccordionDetails>
                  <TextField
                    label={t(`${SCREEN}.altNm`)}
                    name="altNm"
                    type="text"
                    value={guest.altNm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                  <TextField
                    label={t(`${SCREEN}.firstNm`)}
                    name="firstNm"
                    type='text'
                    value={guest.firstNm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                  <TextField
                    label={t(`${SCREEN}.contact`)}
                    name="contact"
                    type="text"
                    value={guest.contact}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="rcountryId-label">{t(`${SCREEN}.rcountryId`)}</InputLabel>
                    <Select
                      labelId="rcountryId-label"
                      label={t(`${SCREEN}.rcountryId`)}
                      name="rcountryId"
                      value={guest.rcountryId ?? 1604}
                      onChange={(e: SelectChangeEvent<number | null>) => handleChangeSelect(e, guest.id)}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Box m={2} />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="country-label">{t(`${SCREEN}.country`)}</InputLabel>
                    <Select
                      labelId="country-label"
                      label={t(`${SCREEN}.country`)}
                      name="countryId"
                      value={guest.countryId ?? 1604}
                      onChange={(e: SelectChangeEvent<number | null>) => handleChangeSelect(e, guest.id)}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.id} value={country.id}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label={t(`${SCREEN}.postNo`)}
                    name="postNo"
                    value={guest.postNo}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    inputProps={{ maxLength: 7 }}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                  {prefectures.some(pref => pref.grpId === guest.countryId) &&
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="provinceId-label">{t(`${SCREEN}.provinceId`)}</InputLabel>
                      <Select
                        labelId="provinceId-label"
                        label={t(`${SCREEN}.provinceId`)}
                        name="provinceId"
                        value={guest.provinceId}
                        onChange={(e: SelectChangeEvent<number | null>) => handleChangeSelect(e, guest.id)}
                      >
                        {prefectures.map((prefecture) => {
                          if (prefecture.grpId === guest.countryId) {
                            return (
                              <MenuItem key={prefecture.id} value={prefecture.id}>
                                {prefecture.name}
                              </MenuItem>
                            )
                          };
                        })}
                      </Select>
                    </FormControl>
                  }
                  <TextField
                    label={t(`${SCREEN}.address1`)}
                    name="address1"
                    type="text"
                    value={guest.address1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                  <TextField
                    label={t(`${SCREEN}.email`)}
                    name="email"
                    type="email"
                    value={guest.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeGuest(e, guest.id)}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                  />
                </AccordionDetails>
              </Accordion>
            </div>
          )}
          <TextField
            label={t(`${SCREEN}.receipt`)}
            name="receipt"
            value={input.receipt}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e)}
            fullWidth
            variant="outlined"
            margin="normal"
          />
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="outlined"
              color="primary"
              // startIcon={<AddIcon />}
              onClick={handleAddGuest}
              style={{ marginTop: '1rem' }}
            >
              ＋
            </Button>
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            style={{ marginTop: '1rem' }}
          >
            {t(`${SCREEN}.next`)}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Input;
